import {debounce} from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  mediaFullscreenModalClosed,
  mediaFullscreenModalImageZoom,
  mediaFullscreenModalOpened,
} from '../../../services/analytics/productEvents';
import {useViewer} from '../../../state/viewer/hooks';
import {
  recordRumAction,
  recordRumDurationVital,
} from '../../../util/datadog/datadogRum';
import {keyDisplayName} from '../../../util/runs';
import {
  createTileMediaSpec,
  type RunWithHistoryAndMediaWandb,
  type TileMediaSpec,
} from '../../MediaCard';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {type Zoom} from '../controls/ZoomControl';
import {type StepMeta} from '../types';
import {
  type CompareModeRunPair,
  getCompareModeRunPair,
} from '../utils/compareEntryConfig';
import {runHasMediaKey} from '../utils/runUtils';
import {type ModalData, type ZoomSource} from './types';

type ModalContextValue = {
  // State management
  isOpen: boolean;
  modalData: ModalData | null;

  // Actions
  openModal: (data: ModalData) => void;
  updateModalData: (data: ModalData) => void;
  closeModal: () => void;

  // Navigation actions
  selectRun: (run: RunWithHistoryAndMediaWandb) => void;
  navigateToPrevious: () => void;
  navigateToNext: () => void;
  compareCurrentRunWithNextRun: () => void;

  // Step/Index actions
  updateStep: (step: number) => void;
  updateIndex: (index: number) => void;
  debouncedUpdateStep: (step: number) => void;
  debouncedUpdateIndex: (index: number) => void;

  // Processed data for components
  currentTileMediaSpec: TileMediaSpec | null;
  navigation: {
    hasNext: boolean;
    hasPrevious: boolean;
    currentRun: RunWithHistoryAndMediaWandb | null;
    availableRuns: RunWithHistoryAndMediaWandb[];
    canCompareCurrentRunWithNextRun: boolean;
  };
  sliderData: {
    stepSlider: {
      min: number;
      max: number;
      current: number;
      steps: number[];
      key: string;
      label: string;
      keyIsStep: boolean;
      allowAnyValue: boolean;
      sliderStepMap?: Map<number, number> | null;
    } | null;
    indexSlider: {
      min: number;
      max: number;
      current: number;
      indices: number[];
    } | null;
  };
  sliderConfig: {
    snapToExistingStep: boolean;
    stepStrideLength: number;
  };

  // Zoom control
  zoom: Zoom;
  setZoom: (zoom: Zoom, source: ZoomSource) => void;
  zoomToFitPercentage: number | null; // percentage to display when zooming to fit
  setZoomToFitPercentage: (percentage: number | null) => void;
};

const ModalContext = createContext<ModalContextValue | null>(null);

export const useMediaBrowserModalContext = (): ModalContextValue => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserModalContext must be used within a MediaBrowserModalProvider'
    );
  }
  return context;
};

type MediaBrowserModalProviderProps = {
  children: React.ReactNode;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  sliderConfig: {
    snapToExistingStep: boolean;
    stepStrideLength: number;
  };
  stepMeta?: StepMeta;
  onModalClose?: (finalStep: number, finalIndex: number) => void;
  onCompareRuns?: (runPair: CompareModeRunPair, mediaKey?: string) => void;
};

export const MediaBrowserModalProvider: React.FC<
  MediaBrowserModalProviderProps
> = ({
  children,
  runsWithMedia,
  sliderConfig,
  stepMeta: initialStepMeta,
  onModalClose,
  onCompareRuns,
}) => {
  // use ref to avoid re-rendering the panel when the modal is opened/closed
  const isOpenRef = useRef(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [selectedRun, setSelectedRun] =
    useState<RunWithHistoryAndMediaWandb | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  // tracking the time at which the modal opened so we can measure the duration of the modal being open
  const modalOpenPerformanceMs = useRef<number | null>(null);
  const modalOpenStartTimeMs = useRef<number | null>(null);

  const stepMeta = initialStepMeta;

  // tracking parameters
  const user = useViewer()?.username;
  const panelId = useStablePanelId();

  const debouncedUpdateStep = useMemo(
    () =>
      debounce((step: number) => {
        setCurrentStep(step);
      }, 16),
    []
  );

  const debouncedUpdateIndex = useMemo(
    () =>
      debounce((index: number) => {
        setCurrentIndex(index);
      }, 16),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateStep.cancel();
      debouncedUpdateIndex.cancel();
    };
  }, [debouncedUpdateStep, debouncedUpdateIndex]);

  const openModal = useCallback(
    (data: ModalData) => {
      debouncedUpdateStep.flush();
      debouncedUpdateIndex.flush();

      setModalData(data);
      const initialRun = runsWithMedia.find(
        run => run.name === data.runSignature.runName
      );
      setSelectedRun(initialRun ?? null);
      setCurrentStep(data.tileMediaSpec.mediaStep);
      setCurrentIndex(data.tileMediaSpec.mediaIndex);

      // Only track the modal open event if the modal is not already open
      if (isOpenRef.current !== true) {
        isOpenRef.current = true;
        modalOpenPerformanceMs.current = performance.now();
        modalOpenStartTimeMs.current = Date.now();
        mediaFullscreenModalOpened({
          metricKey: data.tileMediaSpec.mediaKey,
          entityName: data.runSignature.entityName,
          projectName: data.runSignature.projectName,
          user: user,
          panelId: panelId,
        });
      }
    },
    [
      runsWithMedia,
      debouncedUpdateStep,
      debouncedUpdateIndex,
      user,
      panelId,
      isOpenRef,
    ]
  );

  const selectRun = useCallback(
    (run: RunWithHistoryAndMediaWandb) => {
      setSelectedRun(run);

      if (stepMeta) {
        const runStepData = stepMeta.runs.find(r => r.id === run.name);
        if (runStepData) {
          const clampedStep = Math.max(
            runStepData.min,
            Math.min(currentStep, runStepData.max)
          );
          setCurrentStep(clampedStep);

          const clampedIndex = Math.max(
            runStepData.minIndex,
            Math.min(currentIndex, runStepData.maxIndex)
          );
          setCurrentIndex(clampedIndex);
        }
      }
    },
    [stepMeta, currentStep, currentIndex]
  );

  const updateStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const updateIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Compute step slider data (only recalculates when run or step meta changes)
  const stepSliderData = useMemo(() => {
    if (!selectedRun || !stepMeta) {
      return null;
    }

    const selectedRunStepData = stepMeta.runs.find(
      runData => runData.id === selectedRun.name
    );

    if (!selectedRunStepData) {
      return null;
    }

    return {
      min: selectedRunStepData.minSliderStep,
      max: selectedRunStepData.maxSliderStep,
      steps: selectedRunStepData.sliderSteps,
      key: stepMeta.sliderKey,
      label: keyDisplayName({section: 'summary', name: stepMeta.sliderKey}),
      keyIsStep: stepMeta.sliderKey === '_step',
      allowAnyValue: !sliderConfig.snapToExistingStep,
      sliderStepMap: selectedRunStepData.sliderStepMap,
      stepStrideLength: sliderConfig.stepStrideLength,
    };
  }, [selectedRun, stepMeta, sliderConfig]);

  const currentTileMediaSpec = useMemo((): TileMediaSpec | null => {
    if (!modalData || !selectedRun) {
      return null;
    }

    const mappedStep = stepSliderData?.sliderStepMap?.get(currentStep);

    return createTileMediaSpec({
      ...modalData.tileMediaSpec,
      mediaStep: mappedStep ?? currentStep,
      mediaIndex: currentIndex,
      run: selectedRun,
      runSignature: {
        entityName: selectedRun.entityName,
        projectName: selectedRun.projectName,
        runName: selectedRun.name,
      },
    });
  }, [modalData, selectedRun, currentStep, currentIndex, stepSliderData]);

  const runsWithCurrentMediaKey = useMemo(() => {
    return runsWithMedia.filter(run =>
      runHasMediaKey(run, currentTileMediaSpec?.mediaKey)
    );
  }, [runsWithMedia, currentTileMediaSpec?.mediaKey]);

  const currentRunIndex = useMemo(() => {
    if (!selectedRun || !runsWithCurrentMediaKey.length) {
      return 0;
    }
    return runsWithCurrentMediaKey.findIndex(
      run => run.name === selectedRun?.name
    );
  }, [selectedRun, runsWithCurrentMediaKey]);

  const navigateToPrevious = useCallback(() => {
    if (currentRunIndex > 0) {
      const previousRun = runsWithCurrentMediaKey[currentRunIndex - 1];
      selectRun(previousRun);
    }
  }, [currentRunIndex, runsWithCurrentMediaKey, selectRun]);

  const navigateToNext = useCallback(() => {
    if (
      currentRunIndex >= 0 &&
      currentRunIndex < runsWithCurrentMediaKey.length - 1
    ) {
      const nextRun = runsWithCurrentMediaKey[currentRunIndex + 1];
      selectRun(nextRun);
    }
  }, [currentRunIndex, runsWithCurrentMediaKey, selectRun]);

  // Compute navigation state
  const hasNext =
    currentRunIndex >= 0 &&
    currentRunIndex < runsWithCurrentMediaKey.length - 1;
  const hasPrevious = currentRunIndex > 0;

  const compareRunPair = useMemo(
    () =>
      getCompareModeRunPair(
        runsWithCurrentMediaKey,
        selectedRun?.name,
        currentTileMediaSpec?.mediaKey
      ),
    [runsWithCurrentMediaKey, selectedRun, currentTileMediaSpec?.mediaKey]
  );

  const indexSliderData = useMemo(() => {
    if (!selectedRun || !stepMeta) {
      return null;
    }

    const selectedRunStepData = stepMeta.runs.find(
      runData => runData.id === selectedRun.name
    );

    if (!selectedRunStepData) {
      return null;
    }

    return {
      min: selectedRunStepData.minIndex,
      max: selectedRunStepData.maxIndex,
      indices: selectedRunStepData.indices,
    };
  }, [selectedRun, stepMeta]);

  const sliderData = useMemo(() => {
    if (!stepSliderData || !indexSliderData) {
      return {stepSlider: null, indexSlider: null};
    }

    const mappedCurrentStep =
      Array.from(stepSliderData.sliderStepMap?.entries() ?? []).find(
        ([_, val]) => val === currentStep
      )?.[0] ?? currentStep;
    const stepSlider = {
      ...stepSliderData,
      current: mappedCurrentStep,
    };

    const indexSlider = {
      ...indexSliderData,
      current: currentIndex,
    };

    return {stepSlider, indexSlider};
  }, [stepSliderData, indexSliderData, currentStep, currentIndex]);

  const closeModal = useCallback(() => {
    if (onModalClose) {
      // the step slider converts to the correct step value associated with the slider key
      // according to the sliderStepMap
      onModalClose(sliderData.stepSlider?.current ?? 0, currentIndex);
    }

    debouncedUpdateStep.flush();
    debouncedUpdateIndex.flush();

    setModalData(null);
    setSelectedRun(null);
    setCurrentStep(0);
    setCurrentIndex(0);

    if (
      modalOpenPerformanceMs.current != null &&
      modalOpenStartTimeMs.current != null &&
      isOpenRef.current !== false
    ) {
      isOpenRef.current = false;
      recordRumDurationVital('media_browser_modal_open', {
        duration: performance.now() - modalOpenPerformanceMs.current,
        startTime: modalOpenStartTimeMs.current,
      });

      // Also log to datawarehouse analytics so that datascience can tie it to NPS scores
      mediaFullscreenModalClosed({
        metricKey: modalData?.tileMediaSpec.mediaKey,
        entityName: modalData?.runSignature.entityName,
        projectName: modalData?.runSignature.projectName,
        user: user,
        panelId: panelId,
        duration: performance.now() - modalOpenPerformanceMs.current,
        startTime: modalOpenStartTimeMs.current,
      });
    }

    modalOpenPerformanceMs.current = null;
    modalOpenStartTimeMs.current = null;
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [
    sliderData.stepSlider,
    onModalClose,
    currentIndex,
    debouncedUpdateStep,
    debouncedUpdateIndex,
    modalData,
    user,
    panelId,
    isOpenRef,
  ]);

  const compareCurrentRunWithNextRun = useCallback(() => {
    if (onCompareRuns == null || compareRunPair == null) {
      return;
    }

    closeModal();
    onCompareRuns(compareRunPair, currentTileMediaSpec?.mediaKey);
  }, [
    closeModal,
    compareRunPair,
    currentTileMediaSpec?.mediaKey,
    onCompareRuns,
  ]);

  const [zoom, setZoomState] = useState<Zoom>('fit');
  const [zoomToFitPercentage, setZoomToFitPercentage] = useState<number | null>(
    null
  );

  const setZoom = useCallback(
    (newZoom: Zoom, source: ZoomSource) => {
      setZoomState(newZoom);
      const eventData = {
        prevZoom: zoom,
        newZoom,
        zoomToFitPercentage,
        source,
      };
      recordRumAction('full_screen_modal_image_zoom', eventData);
      mediaFullscreenModalImageZoom(eventData);
    },
    [zoom, zoomToFitPercentage]
  );

  const value: ModalContextValue = useMemo(
    () => ({
      // State management
      // oxlint-disable-next-line react/refs
      isOpen: isOpenRef.current,
      modalData,

      // Actions
      openModal,
      updateModalData: setModalData,
      closeModal,

      // Navigation actions
      selectRun,
      navigateToPrevious,
      navigateToNext,
      compareCurrentRunWithNextRun,

      // Step/Index actions
      updateStep,
      updateIndex,
      debouncedUpdateStep,
      debouncedUpdateIndex,

      // Processed data for components
      currentTileMediaSpec,
      navigation: {
        hasNext,
        hasPrevious,
        currentRun: selectedRun,
        availableRuns: runsWithCurrentMediaKey,
        canCompareCurrentRunWithNextRun:
          onCompareRuns != null && compareRunPair != null,
      },
      sliderData,
      sliderConfig,
      zoom,
      setZoom,
      zoomToFitPercentage,
      setZoomToFitPercentage,
    }),
    [
      isOpenRef,
      modalData,
      openModal,
      closeModal,
      selectRun,
      navigateToPrevious,
      navigateToNext,
      compareCurrentRunWithNextRun,
      updateStep,
      updateIndex,
      debouncedUpdateStep,
      debouncedUpdateIndex,
      currentTileMediaSpec,
      hasNext,
      hasPrevious,
      selectedRun,
      runsWithCurrentMediaKey,
      onCompareRuns,
      compareRunPair,
      sliderData,
      sliderConfig,
      setZoom,
      zoom,
      zoomToFitPercentage,
    ]
  );

  return (
    // oxlint-disable-next-line react/refs
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};
